import React, { useEffect, useId, useRef, useState } from "react";
import { Modal } from "reactstrap";
import { Button } from "components/ui/Button";
import { ReactComponent as CrossIcon } from "Assets/icon/cross-round.svg";
import { useTranslation } from "lib/i18n/use-translation";
import { logError } from "utility/Function";
import ShareCard, { ShareCardProps } from "./ShareCard";
import { canShareImageFiles, downloadFile, makeSharePng } from "./shareImage";
import styles from "./SharePreview.module.scss";

/** What left, in the words "Report Score Shared" records it under. */
export type ImageShareMethod = "Image share" | "Image download";

export type { ShareCardProps };

interface SharePreviewProps {
  card: ShareCardProps;
  onClose: () => void;
  /** The image left the device, by the share sheet or a download. */
  onShared: (method: ImageShareMethod) => void;
  /**
   * The image could not be made and the learner asked for the text instead.
   * Called within their press, so the share sheet or clipboard still accepts
   * it. Absent where the browser has neither, and the button then stays
   * disabled rather than accepting a press it would drop.
   */
  onShareText?: () => void;
}

/**
 * The card drawn on the page, and made into a PNG once it is: only the PNG
 * leaves the device. The `<svg>` is found inside a wrapper.
 */
const PreparedCard: React.FC<{
  card: ShareCardProps;
  onReady: (png: File) => void;
  onFailed: (error: unknown) => void;
}> = ({ card, onReady, onFailed }) => {
  const wrapper = useRef<HTMLDivElement>(null);
  const handlers = useRef({ onReady, onFailed });
  handlers.current = { onReady, onFailed };

  useEffect(() => {
    const svg = wrapper.current?.querySelector("svg");
    if (!svg) {
      handlers.current.onFailed(new Error("The share card drew no <svg>"));
      return undefined;
    }
    let current = true;
    makeSharePng(svg).then(
      (png) => current && handlers.current.onReady(png),
      (error) => current && handlers.current.onFailed(error)
    );
    return () => {
      current = false;
    };
  }, []);

  return (
    <div ref={wrapper} className={styles.card}>
      <ShareCard {...card} />
    </div>
  );
};

/**
 * The card the learner is about to share, previewed before anything leaves
 * the device. The PNG is made as soon as the card is drawn, so Share hands it
 * to the share sheet within the press, which Safari insists on.
 *
 * A browser whose share sheet takes the PNG gets Share; one that cannot gets
 * Download image. If the PNG cannot be made, the button offers the score as
 * text instead, on a fresh press, since the one that opened the preview has
 * been spent by then.
 */
const SharePreview: React.FC<SharePreviewProps> = ({
  card,
  onClose,
  onShared,
  onShareText,
}) => {
  const { t } = useTranslation(["assessment"]);
  const titleId = useId();
  const [canShare] = useState(canShareImageFiles);
  const [png, setPng] = useState<File>();
  const [failed, setFailed] = useState(false);
  const [sharing, setSharing] = useState(false);
  const sharingRef = useRef(false);

  const fail = (error: unknown) => {
    logError("Could not make the report share image", error);
    setFailed(true);
  };

  const share = async (file: File) => {
    // A second press while the sheet is up would be refused by the browser.
    if (sharingRef.current) return;
    sharingRef.current = true;
    setSharing(true);
    try {
      await navigator.share({ files: [file] });
    } catch (error) {
      // Dismissing the sheet is a choice, not a fault: the preview stays up.
      if ((error as Error)?.name !== "AbortError") {
        logError("Could not share the report image", error);
      }
      return;
    } finally {
      sharingRef.current = false;
      setSharing(false);
    }
    onShared("Image share");
  };

  const press = () => {
    if (failed) {
      onShareText?.();
      return;
    }
    if (!png) return;
    if (canShare) {
      void share(png);
      return;
    }
    downloadFile(png);
    onShared("Image download");
  };

  let label = t("assessment:report.ori.shareCard.download");
  if (failed) label = t("assessment:report.ori.share");
  else if (canShare) label = t("assessment:report.ori.shareCard.share");

  return (
    <Modal
      isOpen
      centered
      toggle={onClose}
      labelledBy={titleId}
      className={styles.modal}
      contentClassName={styles.content}
    >
      <div
        aria-busy={(!png && !failed) || sharing || undefined}
        className={styles.body}
      >
        <h2 id={titleId} className={styles["visually-hidden"]}>
          {t("assessment:report.ori.shareCard.previewLabel")}
        </h2>
        <div className={styles.stage}>
          <PreparedCard card={card} onReady={setPng} onFailed={fail} />
          <button
            type="button"
            className={styles.close}
            aria-label={t("assessment:report.ori.shareCard.close")}
            onClick={onClose}
          >
            <CrossIcon aria-hidden className={styles["close-glyph"]} />
          </button>
        </div>
        <Button
          variant="enclosed-primary"
          fullWidth
          disabled={failed ? !onShareText : !png || sharing}
          onClick={press}
        >
          {label}
        </Button>
      </div>
    </Modal>
  );
};

export default SharePreview;
